import { Modal, PageLoader } from '@ver-uds/react';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import DashboardLinkModalForm from './DashboardLinkModalForm/DashboardLinkModalForm';
import DashboardLinkModalUpdateUser from './DashboardLinkModalUpdateUser/DashboardLinkModalUpdateUser';
import { useLoginDotGovVisAuthService } from '../../../services/VisAuthService';
import { useEnvironment } from '../../../context/Environment/EnvironmentContext';
import AuthScopes from '../../../constants/AuthScopes';
import { LinkModalFormValues, linkUserAccountErrors, UpdateUserInfo } from '../../../constants/Dashboard';

export type DashboardLinkModalProps = {
  isOpen: boolean;
  setIsOpen: (show: boolean) => void;
  selectedUser: string;
  setSelectedUser: (logonId: string) => void;
  triggerPasswordReset: () => void;
};

const DashboardLinkModal = ({
  isOpen,
  setIsOpen,
  selectedUser,
  setSelectedUser,
  triggerPasswordReset,
}: DashboardLinkModalProps): JSX.Element => {
  const [updateUserInfo, setUpdateUserInfo] = useState<UpdateUserInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Get vis auth service and configuration information
  const { LOGIN_DOT_GOV_AUTH_CLIENTID } = useEnvironment();
  const visAuthService = useLoginDotGovVisAuthService();

  // Define form
  const formMethods = useForm<LinkModalFormValues>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  // Set modal's app element
  if (!document.getElementById('root')) {
    const root = document.createElement('div');
    root.id = 'root';
    document.body.appendChild(root);
    Modal.setAppElement('#root');
  }

  // Close and reset all values
  const onClose = (): void => {
    setIsOpen(false);
    setSelectedUser('');
    setUpdateUserInfo(null);
    setErrorMessage('');
    formMethods.reset();
  };

  // Performs migration redirect
  const doMigration = async (logonId: string, migrationToken: string): Promise<void> => {
    try {
      // Add logon ID to local storage to show success alert
      setIsLoading(true);
      localStorage.setItem('MFALinkUserLogonId', logonId);

      // Migrate and redirect to login.gov
      await visAuthService.authorizeMigrateRedirect(
        migrationToken,
        LOGIN_DOT_GOV_AUTH_CLIENTID,
        AuthScopes.LOGIN_DOT_GOV,
      );
    } catch (e) {
      // Remove logon ID from local storage and show error on failure
      localStorage.removeItem('MFALinkUserLogonId');
      setErrorMessage(linkUserAccountErrors.general);
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <PageLoader label="Loading" />}
      <Modal
        id="link-user-account-modal"
        header={updateUserInfo ? 'User Account Update' : 'Link Existing User Account'}
        open={isOpen && !isLoading}
        close={onClose}
      >
        {updateUserInfo ? (
          <DashboardLinkModalUpdateUser
            updateUserInfo={updateUserInfo}
            errorMessage={errorMessage}
            doMigration={doMigration}
            onClose={onClose}
          />
        ) : (
          <DashboardLinkModalForm
            formMethods={formMethods}
            errorMessage={errorMessage}
            selectedUser={selectedUser}
            setErrorMessage={setErrorMessage}
            setUpdateUserInfo={setUpdateUserInfo}
            setIsLoading={setIsLoading}
            doMigration={doMigration}
            triggerPasswordReset={triggerPasswordReset}
            onClose={onClose}
          />
        )}
      </Modal>
    </>
  );
};

export default DashboardLinkModal;
